import React, { useState, useEffect } from 'react';
import theme from '../../../theme/globalTheme';
import {
  Card, Input, DatePicker, Select, Button, Table, Modal,
  message, Space, Tag, Descriptions, Divider, Row, Col,
  Popconfirm, Typography, Badge, Tooltip, Alert, Statistic,
  Upload, Image
} from 'antd';
import {
  PlusOutlined, EyeOutlined, MailOutlined, CalendarOutlined,
  LockOutlined, DollarOutlined, CheckCircleOutlined,
  ClockCircleOutlined, ExclamationCircleOutlined, TrophyOutlined,
  PhoneOutlined, FileTextOutlined, SendOutlined,
  DownloadOutlined, PrinterOutlined
} from '@ant-design/icons';
import { supabase } from '../../../api/supabase';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';

const { Title, Text } = Typography;

const LunchReservationSystem = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [lunchReservations, setLunchReservations] = useState([]);
  const [lunchMenuItems, setLunchMenuItems] = useState([]);
  const [lunchFrozenDates, setLunchFrozenDates] = useState([]); // Now managed in-memory
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [contractModalVisible, setContractModalVisible] = useState(false);
  const [stats, setStats] = useState({});

  // États pour les formulaires
  const [createForm, setCreateForm] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    charter_date: null,
    boat_name: '',
    trip_type: ''
  });
  const [dateForm, setDateForm] = useState({
    freeze_date: null,
    reason: ''
  });

  // ✅ DATES GELÉES EN CONFIGURATION (remplace la table supprimée)
  const defaultFrozenDates = [
    { date: '2024-12-25', reason: 'Noël' },
    { date: '2024-12-31', reason: 'Réveillon' },
    { date: '2024-01-01', reason: 'Jour de l\'An' },
    { date: '2024-07-14', reason: 'Fête Nationale' },
    { date: '2024-08-15', reason: 'Assomption' }
  ];

  // Types de voyage
  const tripTypes = [
    { value: 'half_day_morning', label: 'Demi-journée Matin' },
    { value: 'half_day_afternoon', label: 'Demi-journée Après-midi' },
    { value: 'full_day', label: 'Journée Complète' },
    { value: 'sunset', label: 'Sunset' },
    { value: 'transfer', label: 'Transfert' }
  ];

  // Status colors et labels
  const statusColors = {
    initialized: 'default',
    selected: 'processing', 
    signed: 'warning',
    paid: 'success',
    confirmed: 'success',
    cancelled: 'error',
    blocked: 'error'
  };

  const statusLabels = {
    initialized: 'Initialisé',
    selected: 'Sélectionné',
    signed: 'Signé (En attente validation)',
    paid: 'Payé (En attente validation)',
    confirmed: 'Confirmé ✅',
    cancelled: 'Annulé',
    blocked: 'Bloqué'
  };

  useEffect(() => {
    console.log('🚀 LunchReservationSystem - Master Ski Pilou CRM avec Validation');
    fetchLunchReservations();
    fetchLunchMenuItems();
    initializeFrozenDates();
    calculateStats();
  }, []);

  // ✅ FIXED: Separate queries to avoid relationship issues
  const fetchLunchReservations = async () => {
    setLoading(true);
    try {
      console.log('🔍 Fetching all lunch reservations...');
      
      // Get main reservations only
      const { data: reservations, error: reservationsError } = await supabase
        .from('lunch_reservations')
        .select('*')
        .order('created_at', { ascending: false });

      if (reservationsError) {
        console.error('❌ Error fetching reservations:', reservationsError);
        throw reservationsError;
      }

      if (!reservations || reservations.length === 0) {
        console.log('ℹ️ No reservations found');
        setLunchReservations([]);
        return;
      }

      console.log('✅ Reservations loaded:', reservations.length);

      // For each reservation, get items and contracts separately
      const enrichedReservations = await Promise.all(
        reservations.map(async (reservation) => {
          try {
            // Get items for this reservation
            const { data: items, error: itemsError } = await supabase
              .from('lunch_reservation_items')
              .select('*')
              .eq('lunch_reservation_id', reservation.id);

            if (itemsError) {
              console.warn('⚠️ Error loading items for reservation', reservation.id, ':', itemsError.message);
            }

            // Get contracts for this reservation
            const { data: contracts, error: contractsError } = await supabase
              .from('lunch_signed_contracts')
              .select('*')
              .eq('lunch_reservation_id', reservation.id);

            if (contractsError) {
              console.warn('⚠️ Error loading contracts for reservation', reservation.id, ':', contractsError.message);
            }

            return {
              ...reservation,
              lunch_reservation_items: items || [],
              lunch_signed_contracts: contracts || []
            };
          } catch (error) {
            console.warn('⚠️ Error enriching reservation', reservation.id, ':', error.message);
            return {
              ...reservation,
              lunch_reservation_items: [],
              lunch_signed_contracts: []
            };
          }
        })
      );

      setLunchReservations(enrichedReservations);
      console.log('✅ All reservations enriched successfully');

    } catch (error) {
      console.error('❌ Error fetching reservations:', error);
      message.error('Erreur lors du chargement des réservations: ' + error.message);
      setLunchReservations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLunchMenuItems = async () => {
    try {
      const { data, error } = await supabase
        .from('lunch_menu_items')
        .select('*')
        .eq('available', true)
        .order('category');

      if (error) throw error;
      setLunchMenuItems(data || []);
      console.log('✅ Menu items loaded:', data?.length || 0);
    } catch (error) {
      console.error('❌ Error fetching menu:', error);
      message.error('Erreur lors du chargement du menu: ' + error.message);
    }
  };

  // ✅ FIXED: Initialize frozen dates from configuration instead of database
  const initializeFrozenDates = () => {
    // Get frozen dates from localStorage if available, otherwise use defaults
    const savedFrozenDates = localStorage.getItem('lunch_frozen_dates');
    if (savedFrozenDates) {
      try {
        const parsed = JSON.parse(savedFrozenDates);
        setLunchFrozenDates(parsed);
        console.log('✅ Frozen dates loaded from localStorage:', parsed.length);
      } catch (error) {
        console.warn('⚠️ Error parsing saved frozen dates, using defaults');
        setLunchFrozenDates(defaultFrozenDates);
      }
    } else {
      setLunchFrozenDates(defaultFrozenDates);
      console.log('✅ Using default frozen dates:', defaultFrozenDates.length);
    }
  };

  // ✅ FIXED: Save frozen dates to localStorage instead of database
  const saveFrozenDatesToStorage = (dates) => {
    try {
      localStorage.setItem('lunch_frozen_dates', JSON.stringify(dates));
      console.log('💾 Frozen dates saved to localStorage');
    } catch (error) {
      console.error('❌ Error saving frozen dates:', error);
    }
  };

  const calculateStats = async () => {
    try {
      const { data, error } = await supabase
        .from('lunch_reservations')
        .select('status, total_amount, created_at')
        .gte('created_at', dayjs().subtract(30, 'day').toISOString());

      if (error) throw error;

      const totalReservations = data.length;
      const confirmedReservations = data.filter(r => r.status === 'confirmed');
      const totalRevenue = confirmedReservations.reduce((sum, r) => sum + (r.total_amount || 0), 0);
      const conversionRate = totalReservations > 0 ? (confirmedReservations.length / totalReservations * 100).toFixed(1) : 0;

      setStats({
        totalReservations,
        totalRevenue,
        conversionRate,
        pendingReservations: data.filter(r => ['signed', 'paid'].includes(r.status)).length
      });
    } catch (error) {
      console.error('❌ Error calculating stats:', error);
    }
  };

  // Générer un lien unique
  const generateUniqueLink = () => {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  };

  // ✅ CRÉER UNE NOUVELLE RÉSERVATION
  const createLunchReservation = async () => {
    const validationErrors = [];
    if (!createForm.client_name?.trim()) validationErrors.push('Nom client manquant');
    if (!createForm.client_email?.trim()) validationErrors.push('Email manquant');
    if (!createForm.charter_date) validationErrors.push('Date charter manquante');
    if (!createForm.boat_name?.trim()) validationErrors.push('Nom bateau manquant');
    if (!createForm.trip_type) validationErrors.push('Type voyage manquant');

    if (validationErrors.length > 0) {
      message.error('Erreurs de validation: ' + validationErrors.join(', '));
      return;
    }

    setLoading(true);
    try {
      const uniqueLink = generateUniqueLink();
      const expiresAt = dayjs(createForm.charter_date).subtract(48, 'hour').toISOString();

      const reservationData = {
        client_name: createForm.client_name.trim(),
        client_email: createForm.client_email.trim().toLowerCase(),
        client_phone: createForm.client_phone?.trim() || '',
        charter_date: dayjs(createForm.charter_date).format('YYYY-MM-DD'),
        boat_name: createForm.boat_name.trim(),
        trip_type: createForm.trip_type,
        unique_link: uniqueLink,
        expires_at: expiresAt,
        status: 'initialized'
      };

      console.log('📋 Creating reservation with data:', reservationData);

      const { data, error } = await supabase
        .from('lunch_reservations')
        .insert([reservationData])
        .select()
        .single();

      if (error) throw error;

      message.success('Réservation créée avec succès!');
      setCreateForm({
        client_name: '',
        client_email: '',
        client_phone: '',
        charter_date: null,
        boat_name: '',
        trip_type: ''
      });
      
      await fetchLunchReservations();
      await calculateStats();

      // Modal de confirmation
      Modal.info({
        title: '🎉 Réservation Master Ski Pilou créée',
        width: 700,
        content: (
          <div>
            <Alert
              message="📧 Signature Électronique + Paiement VoxPay"
              description="Cette réservation utilise la signature électronique et VoxPay pour le paiement sécurisé."
              type="success"
              showIcon
              style={{ marginBottom: '16px' }}
            />
            
            <p><strong>Lien de réservation :</strong></p>
            <Input.TextArea
              value={`${window.location.origin}/lunch-reservation/${uniqueLink}`}
              readOnly
              rows={2}
              style={{ fontFamily: 'monospace', fontSize: '12px', backgroundColor: theme.colors.glassLight, marginBottom: '16px' }}
            />
            
            <div style={{ marginBottom: '16px' }}>
              <Text strong>Processus:</Text>
              <ol style={{ marginTop: '8px', paddingLeft: '20px' }}>
                <li>Le client sélectionne son menu</li>
                <li>Signature électronique du contrat</li>
                <li>Paiement VoxPay par email/lien web</li>
                <li>Notification reçue dans le CRM</li>
                <li>Validation manuelle par Master Ski Pilou</li>
                <li>Email de confirmation automatique au client</li>
              </ol>
            </div>
            
            <Button 
              type="primary"
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/lunch-reservation/${uniqueLink}`);
                message.success('Lien copié!');
              }}
            >
              📋 Copier le lien
            </Button>
          </div>
        ),
      });

    } catch (error) {
      console.error('❌ Error creating reservation:', error);
      message.error('Erreur: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  // ✅ VALIDER ET CONFIRMER UNE RÉSERVATION
  const confirmReservation = async (reservationId) => {
    try {
      const { error } = await supabase
        .from('lunch_reservations')
        .update({ 
          status: 'confirmed',
          confirmed_at: new Date().toISOString()
        })
        .eq('id', reservationId);

      if (error) throw error;

      // Envoyer email de confirmation au client
      await sendConfirmationEmail(reservationId);

      message.success('Réservation confirmée! Email envoyé au client.');
      fetchLunchReservations();
      calculateStats();

    } catch (error) {
      console.error('❌ Error confirming reservation:', error);
      message.error('Erreur lors de la confirmation: ' + error.message);
    }
  };

  // ✅ ENVOYER EMAIL DE CONFIRMATION AU CLIENT
  const sendConfirmationEmail = async (reservationId) => {
    try {
      const reservation = lunchReservations.find(r => r.id === reservationId);
      if (!reservation) return;

      // Générer le PDF du contrat
      const contractPdf = await generateContractPDF(reservation);

      const emailData = {
        to: reservation.client_email,
        subject: `Confirmation de votre lunch Master Ski Pilou - ${dayjs(reservation.charter_date).format('DD/MM/YYYY')}`,
        message: `
          Bonjour ${reservation.client_name},
          
          Votre réservation lunch pour le ${dayjs(reservation.charter_date).format('DD/MM/YYYY')} est confirmée!
          
          Détails de votre commande:
          ${reservation.lunch_reservation_items?.map(item => 
            `- ${item.item_name}: ${item.quantity} x ${item.unit_price}€`
          ).join('\n')}
          
          Montant total: ${reservation.total_amount}€
          
          Merci de votre confiance.
          
          Master Ski Pilou
          Gustavia, Saint-Barthélemy
        `,
        attachments: [
          {
            filename: `Contrat_MSP_${reservation.client_name}_${dayjs().format('YYYYMMDD')}.pdf`,
            content: contractPdf
          }
        ]
      };

      // Webhook vers Make (Integromat) ou EmailJS
      const webhookUrl = process.env.REACT_APP_MAKE_CONFIRMATION_WEBHOOK_URL;
      if (webhookUrl) {
        await fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(emailData)
        });
        console.log('✅ Confirmation email sent to client');
      } else {
        console.warn('⚠️ Webhook URL not configured - email not sent');
      }

    } catch (error) {
      console.error('❌ Error sending confirmation email:', error);
    }
  };

  // ✅ GÉNÉRER PDF DU CONTRAT SIGNÉ
  const generateContractPDF = async (reservation) => {
    try {
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(20);
      doc.text('MASTER SKI PILOU', 105, 30, { align: 'center' });
      doc.setFontSize(16);
      doc.text('Contrat de Prestation - Lunch', 105, 45, { align: 'center' });
      
      // Date
      doc.setFontSize(12);
      doc.text(`Gustavia, le ${dayjs().format('DD/MM/YYYY')}`, 20, 70);
      
      // Client details
      doc.setFontSize(14);
      doc.text('DÉTAILS CLIENT', 20, 90);
      doc.setFontSize(12);
      doc.text(`Nom: ${reservation.client_name}`, 20, 105);
      doc.text(`Email: ${reservation.client_email}`, 20, 115);
      doc.text(`Téléphone: ${reservation.client_phone || 'Non renseigné'}`, 20, 125);
      doc.text(`Date charter: ${dayjs(reservation.charter_date).format('DD/MM/YYYY')}`, 20, 135);
      doc.text(`Bateau: ${reservation.boat_name}`, 20, 145);
      
      // Articles
      doc.setFontSize(14);
      doc.text('ARTICLES COMMANDÉS', 20, 165);
      doc.setFontSize(12);
      
      let yPos = 180;
      reservation.lunch_reservation_items?.forEach(item => {
        doc.text(`• ${item.item_name}: ${item.quantity} x ${item.unit_price}€ = ${item.total_price}€`, 20, yPos);
        yPos += 10;
      });
      
      // Total
      doc.setFontSize(14);
      doc.text(`TOTAL: ${reservation.total_amount}€`, 20, yPos + 20);
      
      // Signature info
      const contract = reservation.lunch_signed_contracts?.[0];
      if (contract) {
        doc.setFontSize(12);
        doc.text(`Signé électroniquement le: ${dayjs(contract.signature_date).format('DD/MM/YYYY HH:mm')}`, 20, yPos + 40);
      }
      
      // Footer
      doc.setFontSize(10);
      doc.text('MASTER SKI PILOU - Rue Jeanne d\'Arc, GUSTAVIA - 97133 Saint-Barthélemy', 105, 280, { align: 'center' });
      doc.text('Tél: 05 90 27 91 79 - Siret: 48013153100018', 105, 290, { align: 'center' });
      
      return doc.output('dataurlstring');
      
    } catch (error) {
      console.error('❌ Error generating PDF:', error);
      return null;
    }
  };

  // Bloquer/débloquer une réservation
  const toggleReservationBlock = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 'blocked' ? 'initialized' : 'blocked';
      
      const { error } = await supabase
        .from('lunch_reservations')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;

      message.success(`Réservation ${newStatus === 'blocked' ? 'bloquée' : 'débloquée'}`);
      fetchLunchReservations();
    } catch (error) {
      message.error('Erreur modification: ' + error.message);
    }
  };

  // ✅ FIXED: Geler/dégeler une date (localStorage instead of database)
  const toggleDateFreeze = () => {
    if (!dateForm.freeze_date) {
      message.error('Date requise');
      return;
    }

    try {
      const dateString = dayjs(dateForm.freeze_date).format('YYYY-MM-DD');
      const existingDateIndex = lunchFrozenDates.findIndex(fd => fd.date === dateString);
      
      let newFrozenDates;
      if (existingDateIndex >= 0) {
        // Remove the date
        newFrozenDates = lunchFrozenDates.filter((_, index) => index !== existingDateIndex);
        message.success('Date dégelée');
      } else {
        // Add the date
        newFrozenDates = [...lunchFrozenDates, {
          date: dateString,
          reason: dateForm.reason || 'Date bloquée',
          created_by: 'Admin',
          created_at: new Date().toISOString()
        }];
        message.success('Date gelée');
      }

      setLunchFrozenDates(newFrozenDates);
      saveFrozenDatesToStorage(newFrozenDates);
      setDateForm({ freeze_date: null, reason: '' });

    } catch (error) {
      message.error('Erreur modification: ' + error.message);
    }
  };

  // Télécharger le contrat PDF
  const downloadContract = async (reservation) => {
    try {
      const pdfDataUrl = await generateContractPDF(reservation);
      if (pdfDataUrl) {
        const link = document.createElement('a');
        link.href = pdfDataUrl;
        link.download = `Contrat_MSP_${reservation.client_name}_${dayjs().format('YYYYMMDD')}.pdf`;
        link.click();
        message.success('Contrat téléchargé');
      }
    } catch (error) {
      message.error('Erreur téléchargement: ' + error.message);
    }
  };

  // Colonnes du tableau CRM
  const reservationColumns = [
    {
      title: 'Client',
      dataIndex: 'client_name',
      key: 'client_name',
      render: (text, record) => (
        <div>
          <div><strong>{text}</strong></div>
          <div><Text type="secondary" style={{ fontSize: '12px' }}>{record.client_email}</Text></div>
          {record.client_phone && (
            <div><Text type="secondary" style={{ fontSize: '12px' }}>📞 {record.client_phone}</Text></div>
          )}
        </div>
      ),
      width: 200,
    },
    {
      title: 'Date Charter',
      dataIndex: 'charter_date',
      key: 'charter_date',
      render: (date) => dayjs(date).format('DD/MM/YYYY'),
      sorter: (a, b) => dayjs(a.charter_date).unix() - dayjs(b.charter_date).unix(),
      width: 120,
    },
    {
      title: 'Bateau',
      dataIndex: 'boat_name',
      key: 'boat_name',
      width: 120,
    },
    {
      title: 'Type',
      dataIndex: 'trip_type',
      key: 'trip_type',
      render: (type) => {
        const tripType = tripTypes.find(t => t.value === type);
        return <Tag color="blue">{tripType?.label || type}</Tag>;
      },
      width: 140,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <div>
          <Tag color={statusColors[status]}>
            {statusLabels[status]}
          </Tag>
          {record.lunch_signed_contracts?.length > 0 && (
            <div><Tag color="purple" size="small">✍️ Signé</Tag></div>
          )}
        </div>
      ),
      width: 140,
    },
    {
      title: 'Montant',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => amount ? `${amount}€` : '-',
      width: 80,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small" direction="vertical">
          <Space size="small">
            <Tooltip title="Voir détails">
              <Button
                icon={<EyeOutlined />}
                size="small"
                onClick={() => {
                  setSelectedReservation(record);
                  setModalVisible(true);
                }}
              />
            </Tooltip>
            
            <Tooltip title="Copier lien">
              <Button
                icon={<MailOutlined />}
                size="small"
                onClick={() => {
                  const link = `${window.location.origin}/lunch-reservation/${record.unique_link}`;
                  navigator.clipboard.writeText(link);
                  message.success('Lien copié!');
                }}
              />
            </Tooltip>

            {(record.status === 'signed' || record.status === 'paid') && (
              <Tooltip title="Confirmer et envoyer email client">
                <Popconfirm
                  title="Confirmer cette réservation et envoyer l'email de confirmation au client?"
                  onConfirm={() => confirmReservation(record.id)}
                  okText="Oui, confirmer"
                  cancelText="Annuler"
                >
                  <Button
                    icon={<CheckCircleOutlined />}
                    size="small"
                    type="primary"
                    style={{ backgroundColor: theme.colors.lightGrey, borderColor: '#52c41a' }}
                  >
                    ✅ Valider
                  </Button>
                </Popconfirm>
              </Tooltip>
            )}

            {record.lunch_signed_contracts?.length > 0 && (
              <Tooltip title="Télécharger contrat PDF">
                <Button
                  icon={<DownloadOutlined />}
                  size="small"
                  onClick={() => downloadContract(record)}
                />
              </Tooltip>
            )}
          </Space>
          
          <Tooltip title={record.status === 'blocked' ? 'Débloquer' : 'Bloquer'}>
            <Popconfirm
              title={`${record.status === 'blocked' ? 'Débloquer' : 'Bloquer'} cette réservation?`}
              onConfirm={() => toggleReservationBlock(record.id, record.status)}
            >
              <Button
                icon={<LockOutlined />}
                size="small"
                danger={record.status !== 'blocked'}
                type={record.status === 'blocked' ? 'primary' : 'default'}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
      width: 150,
      fixed: 'right',
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgDark,  padding: '0' }}>
      {/* En-tête avec stats */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: '0 0 16px 0' }}>
          🏝️ Master Ski Pilou - CRM Réservations Lunch
        </Title>

        <Alert
          message="📧 Signature Électronique + Validation CRM"
          description="Système complet avec signature électronique, paiement VoxPay et validation manuelle avant envoi de confirmation client."
          type="success"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        
        <Row gutter={16} style={{ marginBottom: '16px' }}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Total Réservations (30j)"
                value={stats.totalReservations || 0}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: theme.colors.white }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="CA Confirmé (30j)"
                value={stats.totalRevenue || 0}
                suffix="€"
                prefix={<DollarOutlined />}
                valueStyle={{ color: theme.colors.white }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Taux Conversion"
                value={stats.conversionRate || 0}
                suffix="%"
                prefix={<TrophyOutlined />}
                valueStyle={{ color: theme.colors.textSecondary }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="⚠️ En Attente Validation"
                value={stats.pendingReservations || 0}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: theme.colors.textSecondary }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {/* Navigation */}
      <div style={{ marginBottom: '24px' }}>
        <Space>
          <Button 
            type={activeTab === 'dashboard' ? 'primary' : 'default'}
            onClick={() => setActiveTab('dashboard')}
          >
            📊 Tableau de Bord
          </Button>
          <Button 
            type={activeTab === 'create' ? 'primary' : 'default'}
            onClick={() => setActiveTab('create')}
          >
            ➕ Créer Réservation
          </Button>
          <Button 
            type={activeTab === 'dates' ? 'primary' : 'default'}
            onClick={() => setActiveTab('dates')}
          >
            🗓️ Gestion Dates
          </Button>
        </Space>
      </div>

      {/* Dashboard */}
      {activeTab === 'dashboard' && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Title level={4} style={{ margin: 0 }}>Liste des Réservations Master Ski Pilou</Title>
            <Space>
              <Badge count={lunchReservations.filter(r => ['signed', 'paid'].includes(r.status)).length}>
                <Button onClick={() => { fetchLunchReservations(); calculateStats(); }} loading={loading}>
                  🔄 Actualiser
                </Button>
              </Badge>
            </Space>
          </div>
          
          <Table
            columns={reservationColumns}
            dataSource={lunchReservations}
            rowKey="id"
            loading={loading}
            pagination={{ 
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Total: ${total} réservations`
            }}
            scroll={{ x: 1400 }}
            size="small"
            rowClassName={(record) => {
              if (['signed', 'paid'].includes(record.status)) {
                return 'reservation-pending-validation';
              }
              return '';
            }}
          />
          
          <style jsx>{`
            .reservation-pending-validation {
              background-color: #fff7e6 !important;
            }
            .reservation-pending-validation:hover {
              background-color: #ffefd1 !important;
            }
          `}</style>
        </Card>
      )}

      {/* Création réservation */}
      {activeTab === 'create' && (
        <Card title="➕ Créer une Nouvelle Réservation">
          <Row gutter={16}>
            <Col span={12}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Nom du Client *
                </label>
                <Input
                  placeholder="Nom complet"
                  value={createForm.client_name}
                  onChange={(e) => setCreateForm({...createForm, client_name: e.target.value})}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Email *
                </label>
                <Input
                  type="email"
                  placeholder="email@exemple.com"
                  value={createForm.client_email}
                  onChange={(e) => setCreateForm({...createForm, client_email: e.target.value})}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Téléphone Client
                </label>
                <Input
                  placeholder="+33 6 12 34 56 78"
                  value={createForm.client_phone}
                  onChange={(e) => setCreateForm({...createForm, client_phone: e.target.value})}
                  prefix={<PhoneOutlined />}
                />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Optionnel
                </Text>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Date du Charter *
                </label>
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  value={createForm.charter_date}
                  onChange={(date) => setCreateForm({...createForm, charter_date: date})}
                  disabledDate={(current) => {
                    const isPast = current && current < dayjs().startOf('day');
                    const isFrozen = lunchFrozenDates.some(fd => 
                      dayjs(fd.date).isSame(current, 'day')
                    );
                    return isPast || isFrozen;
                  }}
                />
              </div>
            </Col>

            <Col span={12}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Bateau *
                </label>
                <Input
                  placeholder="Nom du bateau"
                  value={createForm.boat_name}
                  onChange={(e) => setCreateForm({...createForm, boat_name: e.target.value})}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Type de Journée *
                </label>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Sélectionner le type"
                  value={createForm.trip_type}
                  onChange={(value) => setCreateForm({...createForm, trip_type: value})}
                >
                  {tripTypes.map(type => (
                    <Select.Option key={type.value} value={type.value}>
                      {type.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              <Alert
                message="📧 Processus Automatisé"
                description="Signature électronique → Paiement VoxPay → Notification CRM → Validation manuelle → Email confirmation client avec PDF"
                type="info"
                showIcon
                style={{ marginBottom: '16px' }}
              />

              <Button 
                type="primary" 
                size="large"
                block
                loading={loading}
                icon={<PlusOutlined />}
                onClick={createLunchReservation}
                style={{ marginTop: '24px' }}
              >
                Créer la Réservation
              </Button>
            </Col>
          </Row>
        </Card>
      )}

      {/* Gestion des dates - FIXED */}
      {activeTab === 'dates' && (
        <Row gutter={16}>
          <Col span={12}>
            <Card title="🗓️ Geler une Date">
              <Alert
                message="💾 Stockage Local"
                description="Les dates gelées sont maintenant stockées localement et persisteront dans ce navigateur."
                type="info"
                showIcon
                style={{ marginBottom: '16px' }}
              />

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Date à geler *
                </label>
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  value={dateForm.freeze_date}
                  onChange={(date) => setDateForm({...dateForm, freeze_date: date})}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                  Raison
                </label>
                <Input.TextArea
                  placeholder="Raison du blocage..."
                  value={dateForm.reason}
                  onChange={(e) => setDateForm({...dateForm, reason: e.target.value})}
                  rows={3}
                />
              </div>

              <Button type="primary" block onClick={toggleDateFreeze}>
                ❄️ Geler/Dégeler la Date
              </Button>
            </Card>
          </Col>

          <Col span={12}>
            <Card title="🧊 Dates Gelées">
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {lunchFrozenDates.map((date, index) => (
                  <div key={`${date.date}-${index}`} style={{ 
                    marginBottom: '12px', 
                    padding: '12px', 
                    border: '1px solid #f0f0f0', 
                    borderRadius: '6px',
                    backgroundColor: theme.colors.glassLight
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <strong>{dayjs(date.date).format('DD/MM/YYYY')}</strong>
                        {date.reason && (
                          <div style={{ marginTop: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>{date.reason}</Text>
                          </div>
                        )}
                      </div>
                      <Popconfirm
                        title="Dégeler cette date?"
                        onConfirm={() => {
                          setDateForm({ freeze_date: dayjs(date.date), reason: '' });
                          setTimeout(toggleDateFreeze, 100);
                        }}
                      >
                        <Button size="small" danger>
                          🔥 Dégeler
                        </Button>
                      </Popconfirm>
                    </div>
                  </div>
                ))}
                {lunchFrozenDates.length === 0 && (
                  <Text type="secondary">Aucune date gelée</Text>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Modal détails réservation */}
      <Modal
        title="📋 Détails de la Réservation"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={900}
      >
        {selectedReservation && (
          <div>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="Status">
                <Tag color={statusColors[selectedReservation.status]}>
                  {statusLabels[selectedReservation.status]}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Montant Total">
                {selectedReservation.total_amount || 0}€
              </Descriptions.Item>
              <Descriptions.Item label="Téléphone">
                {selectedReservation.client_phone || 'Non renseigné'}
              </Descriptions.Item>
              <Descriptions.Item label="Créé le">
                {dayjs(selectedReservation.created_at).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
              {selectedReservation.confirmed_at && (
                <Descriptions.Item label="Confirmé le" span={2}>
                  {dayjs(selectedReservation.confirmed_at).format('DD/MM/YYYY HH:mm')}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider>🔗 Lien de Réservation</Divider>
            <Input.TextArea
              value={`${window.location.origin}/lunch-reservation/${selectedReservation.unique_link}`}
              readOnly
              rows={2}
              style={{ fontFamily: 'monospace', fontSize: '12px' }}
            />

            {/* Signature électronique */}
            {selectedReservation.lunch_signed_contracts?.length > 0 && (
              <>
                <Divider>✍️ Signature Électronique</Divider>
                {selectedReservation.lunch_signed_contracts.map(contract => (
                  <div key={contract.id} style={{ marginBottom: '16px' }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text strong>Date de signature: </Text>
                        <Text>{dayjs(contract.signature_date).format('DD/MM/YYYY HH:mm')}</Text>
                      </div>
                      
                      {contract.signature_data && (
                        <div>
                          <Text strong>Signature: </Text>
                          <div style={{ 
                            border: `1px solid ${theme.colors.glassBorder}`, 
                            borderRadius: '4px', 
                            padding: '8px',
                            backgroundColor: theme.colors.glassLight,
                            maxWidth: '300px'
                          }}>
                            <img 
                              src={contract.signature_data} 
                              alt="Signature client" 
                              style={{ maxWidth: '100%', height: 'auto' }}
                            />
                          </div>
                        </div>
                      )}
                      
                      <Button 
                        type="primary" 
                        icon={<DownloadOutlined />}
                        onClick={() => downloadContract(selectedReservation)}
                        size="small"
                      >
                        📄 Télécharger Contrat PDF
                      </Button>
                    </Space>
                  </div>
                ))}
              </>
            )}

            {/* Articles sélectionnés */}
            {selectedReservation.lunch_reservation_items?.length > 0 && (
              <>
                <Divider>🛒 Articles Sélectionnés</Divider>
                <Table
                  size="small"
                  dataSource={selectedReservation.lunch_reservation_items}
                  rowKey="id"
                  pagination={false}
                  columns={[
                    { title: 'Article', dataIndex: 'item_name', key: 'item_name' },
                    { title: 'Qté', dataIndex: 'quantity', key: 'quantity', width: 60 },
                    { 
                      title: 'Prix Unit.', 
                      dataIndex: 'unit_price', 
                      key: 'unit_price', 
                      render: (price) => `${price}€`,
                      width: 80
                    },
                    { 
                      title: 'Total', 
                      dataIndex: 'total_price', 
                      key: 'total_price', 
                      render: (price) => `${price}€`,
                      width: 80
                    },
                  ]}
                />
              </>
            )}

            {/* Actions de validation */}
            {(selectedReservation.status === 'signed' || selectedReservation.status === 'paid') && (
              <>
                <Divider>✅ Actions de Validation</Divider>
                <Alert
                  message="⚠️ Validation Requise"
                  description="Cette réservation a été signée et/ou payée par le client. Validez-la pour envoyer l'email de confirmation automatique."
                  type="warning"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
                <Space>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={() => {
                      confirmReservation(selectedReservation.id);
                      setModalVisible(false);
                    }}
                    size="large"
                    style={{ backgroundColor: theme.colors.lightGrey, borderColor: '#52c41a' }}
                  >
                    ✅ Confirmer et Envoyer Email Client
                  </Button>
                  <Text type="secondary">
                    Le client recevra un email de confirmation avec le contrat PDF en pièce jointe.
                  </Text>
                </Space>
              </>
            )}

            {selectedReservation.status === 'confirmed' && (
              <>
                <Divider>✅ Réservation Confirmée</Divider>
                <Alert
                  message="✅ Réservation Validée"
                  description={`Cette réservation a été confirmée le ${dayjs(selectedReservation.confirmed_at).format('DD/MM/YYYY HH:mm')}. Le client a reçu son email de confirmation.`}
                  type="success"
                  showIcon
                />
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LunchReservationSystem;